import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ContentChild,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { LinkComponent } from '../../atoms/link/link.component';
import { AlertType } from '../../../shared/enums/alert-type.enums';

import { IconComponent } from '../../atoms/icon/icon.component';
import { Sizes, Colors } from '../../../shared/enums/shared.enums';

import { Link } from '../../../shared/interfaces/link.interface';
import { HrefType } from '../../../shared/enums/href-type.enums';
import {
  CaasDisruptor,
  CaasLink,
} from '../../../shared/interfaces/caas/caas.interface';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { CapitalizePipe } from '../../capitalize.pipe';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'hdi-alert',
  standalone: true,
  templateUrl: './alert.component.html',
  styleUrl: './alert.component.scss',
  imports: [
    CommonModule,
    FontAwesomeModule,
    LinkComponent,
    IconComponent,
    CapitalizePipe,
    NgxSkeletonLoaderModule,
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class AlertComponent implements OnInit, OnChanges {
  @Input() type: AlertType = AlertType.NOTIFICATION;
  @Input() title: string = 'Wichtige Bekanntmachung';
  @Input() dropdownText: string = '';
  @Input() link?: Link;
  @Input() isDropdownOpen: boolean = false;
  @Input() isAlertClosed: boolean = false;

  @Input() headerIcon: string = 'triangle-exclamation';
  @Input() actionIcon: string = 'arrow-right-long';
  @Input() iconSize: Sizes = Sizes.MEDIUM;
  @Input() isExpandable: boolean = true;
  @Input() href: string = '';
  @Input() caasData: CaasDisruptor = {} as CaasDisruptor;

  alertClass: string = '';
  alertType = AlertType;
  Sizes = Sizes;
  HrefType = HrefType;
  isWithinTimeframe: boolean = false;
  public isLoading = true;

  @Output() closed = new EventEmitter<void>();
  @ContentChild(LinkComponent)
  contentRef: LinkComponent = {} as LinkComponent;

  get iconColor(): Colors {
    return this.type === AlertType.WARNING ||
      this.type === AlertType.NOTIFICATION
      ? Colors.BLACK
      : Colors.WHITE;
  }

  constructor(
    public readonly caasBodytextService: CaasBodytextService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnInit(): void {
    this.setAlertClass();
    this.checkTimeframe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData'] && this.caasData) {
      this.setCaasContent();
    }
    if (isPlatformBrowser(this.platformId)) {
      this.isLoading = false;
    }
  }

  setAlertClass(): void {
    this.alertClass = '';
    if (this.type === this.alertType.NOTIFICATION) {
      this.alertClass = 'notification';
    } else if (this.type === this.alertType.WARNING) {
      this.alertClass = 'warning';
    } else if (this.type === this.alertType.ERROR) {
      this.alertClass = 'error';
    } else if (this.type === this.alertType.SUCCESS) {
      this.alertClass = 'success';
    } else if (this.type === this.alertType.CRITICAL) {
      this.alertClass = 'critical';
    }
  }

  private readonly toggleTimeout: ReturnType<typeof setTimeout> = setTimeout(
    () => {},
    0
  );

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  closeAlert(): void {
    this.isAlertClosed = true;
    this.isDropdownOpen = false;
    this.closed.emit();
  }

  checkTimeframe(): void {
    let startDate: string | undefined;
    let endDate: string | undefined;
    safeAssign(
      () => {
        startDate = this.caasData?.formData?.st_startdate?.value;
      },
      'Assigning startDate',
      this.errorHandler
    );
    safeAssign(
      () => {
        endDate = this.caasData?.formData?.st_enddate?.value;
      },
      'Assigning endDate',
      this.errorHandler
    );
    const now = new Date();
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    this.isWithinTimeframe = (!start || now >= start) && (!end || now <= end);
  }

  handleLinkClick(event: Event, href: string): void {
    // Revert to original handleLinkClick method
    if (href.startsWith('#')) {
      event.preventDefault();
      const targetElement = this.document.querySelector(href);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }
  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleDropdown();
    }
  }
  setCaasContent() {
    safeAssign(
      () => {
        if (this.caasData?.formData?.st_color) {
          this.type = this.setCaasAlertType(
            this.caasData.formData.st_color.value.identifier
          );
        }
      },
      'Assigning Alert Type',
      this.errorHandler
    );
    safeAssign(
      () => {
        if (this.caasData?.formData?.st_header?.value) {
          this.title = this.setCaasTitle(
            this.caasData.formData.st_header.value
          );
        }
      },
      'Assigning Alert Title',
      this.errorHandler
    );
    safeAssign(
      () => {
        if (this.caasData?.formData?.st_bodytext) {
          this.dropdownText = this.setCaasDropdownText(
            this.caasData.formData.st_bodytext.value ?? ''
          );
        }
      },
      'Assigning dropdownText',
      this.errorHandler
    );
    safeAssign(
      () => {
        if (this.caasData?.formData?.st_link?.value?.formData) {
          this.link = this.setCaasLink(this.caasData.formData.st_link);
        }
      },
      'Assigning link',
      this.errorHandler
    );
    safeAssign(
      () => {
        if (this.caasData?.formData?.st_icon) {
          this.headerIcon =
            this.caasBodytextService.getIcon(this.caasData.formData.st_icon) ??
            '';
        }
      },
      'Assigning icon',
      this.errorHandler
    );
  }

  setCaasAlertType(identifier?: string): AlertType {
    switch (identifier) {
      case 'green':
        return AlertType.SUCCESS;
      case 'orange':
        return AlertType.WARNING;
      case 'red':
        return AlertType.ERROR;
      case 'turquoise':
        return AlertType.NOTIFICATION;
      case 'black':
        return AlertType.CRITICAL;
      default:
        return AlertType.NOTIFICATION;
    }
  }

  setCaasTitle(title: string): string {
    return title;
  }

  setCaasDropdownText(text: string): string {
    return this.caasBodytextService.extractTextEditorContent(text ?? '');
  }

  setCaasLink(link: CaasLink): Link {
    let label = '';
    let hrefType: HrefType | undefined;
    let href: string | undefined;
    let title = '';
    safeAssign(
      () => {
        label = link?.value?.formData?.lt_link_label?.value ?? '';
      },
      'Assigning link label',
      this.errorHandler
    );
    safeAssign(
      () => {
        hrefType = this.caasBodytextService.getHrefType(link);
      },
      'Assigning hrefType',
      this.errorHandler
    );
    safeAssign(
      () => {
        href = this.caasBodytextService.foundHref(link);
      },
      'Assigning href',
      this.errorHandler
    );
    safeAssign(
      () => {
        title = link?.value?.formData?.lt_link_title?.value ?? '';
      },
      'Assigning title',
      this.errorHandler
    );
    return {
      label,
      hrefType,
      href,
      target: '_blank',
      title,
    };
  }
}
